import { Trans, useLingui } from '@lingui/react/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useEffect, useMemo } from 'react';
import { SyncLoader } from 'react-spinners';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { history } from 'store/store';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDButton from 'ud-ui/components/button';
import ImgNotFound from 'ud-ui/components/images/not-found.png';
import UDText from 'ud-ui/components/text';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import OrganizerInfo from 'modules/organizers/ui/components/organizer-info';
import Balance from 'modules/organizers/ui/components/balance';
import SwitchOrganizerButton from 'modules/organizers/ui/components/switch-organizer-button';
import { useOrganizerById } from 'modules/organizers/ui/hooks/use-organizer-by-id';

function OrganizerCard() {
  const { t } = useLingui();
  const { info } = useOrganizer();
  const { organizer, isLoading, isError, switchOrganizer } = useOrganizerById();
  const { setHeaderContent } = useMainLayout();

  const isCurrentOrganizer = useMemo(() => {
    return info?.id === organizer?.id;
  }, [info, organizer]);

  const breadcrumbs = useMemo(() => {
    const items = [{ label: t`Организаторы`, to: Pages.ORGANIZERS.INDEX }];

    const { name, id } = organizer ?? {};
    if (name && id) {
      items.push({ label: name, to: Pages.ORGANIZERS.builders.view(id) });
    }

    return items;
  }, [organizer, t]);

  useEffect(() => {
    setHeaderContent(<UDBreadcrumbs breadcrumbs={breadcrumbs} />);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  const isAdmin = useIsAdmin();

  if (!isAdmin) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex-center w-full px-6" style={{ height: '100%', minHeight: 'calc(100vh - 241px)' }}>
        <SyncLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-center w-full px-6" style={{ height: '100%', flexDirection: 'column' }}>
        <img style={{ width: 130, height: 130 }} alt="error" src={ImgNotFound} />
        <UDText type="callout" className="text-surfaceRomanSilver30">
          <Trans>Ошибка загрузки данных. Попробуйте обновить страницу.</Trans>
        </UDText>
      </div>
    );
  }

  const organizerName = organizer?.name;

  return (
    <>
      <div>
        {/* Узкий экран переносит группы строкой, а не сжимает кнопки (IMGM-1610). */}
        <div className="flex w-full flex-row flex-wrap items-center justify-between gap-x-6 gap-y-3 px-6 py-3">
          <div className="flex min-w-0 flex-wrap items-center gap-6">
            <UDText type="headline">
              <Trans>Информация об организаторе</Trans>
            </UDText>
            <UDButton
              onClick={() => history.push(Pages.ORGANIZERS.INDEX)}
              className={'flex-center shrink-0'}
              variant={'primary'}
              style={{ height: 36 }}
            >
              <Trans>Вернуться к списку</Trans>
            </UDButton>
            {!isCurrentOrganizer && <SwitchOrganizerButton label={t`Переключиться на ${organizerName}`} onClick={switchOrganizer} />}
          </div>

          <Balance organizer={organizer} />
        </div>
        <div className="border-surfacePlatinum20 flex w-full items-center border-b px-6" />
        <div className="w-full px-6 py-6">
          <OrganizerInfo organizer={organizer} />
        </div>
      </div>
    </>
  );
}

export default OrganizerCard;
